import React, { useState, useMemo, useContext } from "react";
import {
	Modal,
	Box,
	Typography,
	Grid,
	Button,
	CircularProgress,
	Card,
	CardContent,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Paper,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import CombinedCard from "../Charts.jsx/NewCharts/chartsLogic/CombinedCard";
import {
	PieChartNE,
	PieChartSP,
	BarCharts,
	SECTION_LABELS,
} from "../Charts.jsx/NewCharts/chartsLogic/charts";
import LineChartMUI from "../Charts.jsx/NewCharts/chartsLogic/LineMUICharts";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { TarjetaDiferencia, TarjetaLogrado } from "../Charts.jsx/NewCharts/chartsLogic/charts";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { GlobalContext } from "../../GlobalContext";
import { obtenerDatos } from "../../api/config";

const SHIFT_SECTIONS = ["Diurno", "Nocturno", "Madrugada"];

const shiftLabels = (t) => ({
	Diurno: t("shifts.diurno"),
	Nocturno: t("shifts.nocturno"),
	Madrugada: t("shifts.madrugada"),
});

const DAY_MAP = {
	0: "lunes",
	1: "martes",
	2: "miercoles",
	3: "jueves",
	4: "viernes",
	5: "sabado",
	6: "domingo",
};

const EMPTY_WEEK = {
	lunes: "-",
	martes: "-",
	miercoles: "-",
	jueves: "-",
	viernes: "-",
	sabado: "-",
	domingo: "-",
};

const formatHour = (value) => {
	if (!value) return "";
	return String(value).slice(0, 5);
};

const formatPeriodsList = (periods = []) => {
	if (!Array.isArray(periods) || periods.length === 0) return "-";

	const formatted = periods
		.map((period) => {
			const start = formatHour(period?.start_time);
			const end = formatHour(period?.end_time);

			if (start && end) return `${start} - ${end}`;
			if (start) return start;
			if (end) return end;
			return null;
		})
		.filter(Boolean);

	return formatted.length > 0 ? formatted.join(" / ") : "-";
};

const normalizeContractsByType = (contracts = [], t) => {
	return contracts.reduce(
		(acc, contract) => {
			const tipo = contract.tipo_contrato || t("new_charts.unknown_contract_type");
			const days = { ...EMPTY_WEEK };

			const dataDays = Array.isArray(contract.contracts_per_day) ? contract.contracts_per_day : [];
			const dataPeriods = Array.isArray(contract.contracts_per_periods)
				? contract.contracts_per_periods
				: [];

			dataDays.forEach((dayItem) => {
				const dayKey = DAY_MAP[dayItem.day];
				if (!dayKey) return;

				if (dayItem.value !== 1) {
					days[dayKey] = "-";
					return;
				}

				const periodGroup = dataPeriods.find(
					(periodItem) =>
						periodItem.config_contracts_per_days_id === dayItem.config_contracts_per_days_id,
				);

				days[dayKey] = formatPeriodsList(periodGroup?.periods || []);
			});

			const row = {
				id: contract.id,
				contrato: contract.nombre || contract.kronos_name || "-",
				...days,
			};

			if (!acc[tipo]) acc[tipo] = [];
			acc[tipo].push(row);

			return acc;
		},
		{
			Diurno: [],
			Nocturno: [],
			Madrugada: [],
		},
	);
};

const InfoIconButton = ({ title, placement = "left" }) => (
	<Tooltip arrow placement={placement} title={title}>
		<IconButton
			size="small"
			sx={{
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				width: 24,
				height: 24,
			}}
		>
			<InfoOutlinedIcon fontSize="small" />
		</IconButton>
	</Tooltip>
);

const normalizeResumenData = (contratoMonthlyRaw) => {
	if (!contratoMonthlyRaw || typeof contratoMonthlyRaw !== "object") return [];

	const byType = new Map();

	const ensureType = (tipo) => {
		if (!tipo) return null;
		if (!byType.has(tipo)) {
			byType.set(tipo, { type: tipo, contracts: [] });
		}
		return byType.get(tipo);
	};

	Object.entries(contratoMonthlyRaw).forEach(([tipoContrato, lista]) => {
		if (!Array.isArray(lista)) return;

		lista.forEach((c) => {
			if (!c) return;

			const tipo = c.tipo_contrato || tipoContrato || "Sin tipo";

			const section = ensureType(tipo);
			if (!section) return;

			const real = c.contratados_ne ?? 0;
			const objetivo = c.contratados_sp ?? 0;
			const diferencia = typeof c.diferencia !== "undefined" ? c.diferencia : objetivo - real;

			section.contracts.push({
				id: c.id,
				nombre: c.nombre_contrato || c.kronos_name || `Contrato ${c.id}`,
				real,
				objetivo,
				diferencia,
				recomendacion: c.sugerencia || "",
			});
		});
	});

	return Array.from(byType.values());
};

export function ModalChartHome({
	open,
	onClose,
	monthName,
	data: { mensual = [], lineal = [], mesesSeleccionados: selMeses = [] } = {},
	loading = false,
	totalNE = 0,
	totalSP = 0,
	coberturaReal,
	holguraReal,
	coberturaIdeal,
	holguraIdeal,
	pieData = { datosTortaNE: [], datosTortaSP: [] },
	barData = { Diurno: [], Nocturno: [], Madrugada: [] },
	tableData = [],
	hasIdeal,
	hasReal,
	diferenciasContratos = {},
	diferenciasTabla: { diferencias, logrados } = {},
	summaryData = [],
}) {
	const [view, setView] = useState("resumen");
	const [openDetailModal, setOpenDetailModal] = useState(false);
	const [selectedSection, setSelectedSection] = useState("");
	const [contractDetails, setContractDetails] = useState({
		Diurno: [],
		Nocturno: [],
		Madrugada: [],
	});
	const [contractsLoading, setContractsLoading] = useState(false);

	const { t } = useTranslation();
	const { site_id } = useContext(GlobalContext);
	const labels = useMemo(() => shiftLabels(t), [t]);

	const safeLineal = useMemo(() => (Array.isArray(lineal) ? lineal : []), [lineal]);
	const safeMeses = useMemo(() => (Array.isArray(selMeses) ? selMeses : []), [selMeses]);

	const { datosTortaNE, datosTortaSP } = pieData;
	const barrasPorSeccion = barData;
	const filasTabla = tableData;

	const totalPieNE = useMemo(
		() => (datosTortaNE || []).reduce((acc, item) => acc + (Number(item?.valor) || 0), 0),
		[datosTortaNE],
	);

	const totalPieSP = useMemo(
		() => (datosTortaSP || []).reduce((acc, item) => acc + (Number(item?.valor) || 0), 0),
		[datosTortaSP],
	);

	const TotalPill = ({ label, total }) => (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				px: 2,
				py: 0.75,
				borderRadius: 999,
				bgcolor: "grey.200",
			}}
		>
			<Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
				{label}
			</Typography>
			<Typography variant="body2" sx={{ fontWeight: 700 }}>
				{total}
			</Typography>
		</Box>
	);

	const totalPeopleLabel = useMemo(() => t("new_charts.total_people"), [t]);
	const resumenSections = useMemo(() => normalizeResumenData(summaryData), [summaryData]);

	const monthLabelMap = useMemo(() => {
		return [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		].reduce((map, key, idx) => {
			map[idx + 1] = t(`aux.months.${key}`);
			return map;
		}, {});
	}, [t]);

	const columns = useMemo(
		() => [
			{ field: "nombre", headerName: t("new_charts.name_contract"), width: 180 },
			{
				field: "kronos_name",
				headerName: t("new_charts.kronos_name"),
				width: 150,
			},
			{
				field: "mes",
				headerName: t("new_charts.month"),
				flex: 1,
				valueFormatter: ({ value }) => monthLabelMap[value] ?? value,
			},
			{
				field: "contratados_ne",
				headerName: t("new_charts.initial_real"),
				flex: 1,
			},
			{
				field: "contratados_sp",
				headerName: t("new_charts.objetive"),
				flex: 1,
			},
			{
				field: "diferencia",
				headerName: t("new_charts.diferent"),
				flex: 1,
				renderCell: ({ value }) => {
					const diff = Number(value);
					let color = "text.primary";

					if (!isNaN(diff)) {
						if (diff < 0) color = "error.main";
					}

					return (
						<Box component="span" sx={{ fontWeight: 600, color }}>
							{value}
						</Box>
					);
				},
			},
			{ field: "zcero", headerName: t("new_charts.final_real"), flex: 1 },
			{ field: "logrado", headerName: t("new_charts.achieved"), flex: 1 },
		],
		[t, monthLabelMap],
	);

	const sectionLabel = useMemo(() => {
		const map1 = labels;
		const map2 = SECTION_LABELS(t);

		return (type) => map1?.[type] ?? map2?.[type] ?? type;
	}, [labels, t]);

	const handleOpenDetails = async (seccion) => {
		setSelectedSection(seccion);
		setOpenDetailModal(true);

		if (contractDetails[seccion]?.length > 0) return;

		try {
			setContractsLoading(true);
			const response = await obtenerDatos(`/contracts/get/${site_id}`);
			const normalized = normalizeContractsByType(response || [], t);

			setContractDetails({
				Diurno: normalized.Diurno || [],
				Nocturno: normalized.Nocturno || [],
				Madrugada: normalized.Madrugada || [],
			});
		} catch (error) {
			console.error("Error obteniendo contratos:", error);
		} finally {
			setContractsLoading(false);
		}
	};

	const handleCloseDetails = () => {
		setOpenDetailModal(false);
	};

	return (
		<>
			<Modal
				open={open}
				onClose={onClose}
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
			>
				<Box
					sx={{
						width: "80%",
						maxHeight: "90vh",
						bgcolor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: "16px",
						p: 4,
						overflowY: "auto",
					}}
				>
					<Typography variant="h6" align="center" mb={3}>
						{t("modal_chart_home.title")} – {monthName}
					</Typography>

					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
							<CircularProgress />
						</Box>
					) : (
						<>
							<Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 4 }}>
								{[
									{
										key: "resumen",
										label: t("modal_chart_home.tab.summary.title"),
									},
									{ key: "grafico", label: t("modal_chart_home.tab.graph") },
									{
										key: "contratos",
										label: t("modal_chart_home.tab.contracts"),
									},
									{ key: "tabla", label: t("modal_chart_home.tab.table") },
								].map((tab) => (
									<Button
										key={tab.key}
										onClick={() => setView(tab.key)}
										variant={view === tab.key ? "contained" : "outlined"}
										sx={{
											textTransform: "none",
											borderRadius: "999px",
											border: view === tab.key ? "none" : "1px solid",
											borderColor: view === tab.key ? "transparent" : "divider",
											bgcolor: view === tab.key ? "text.primary" : "transparent",
											color: view === tab.key ? "common.white" : "text.primary",
											px: 3,
											"&:hover": {
												bgcolor: view === tab.key ? "text.primary" : "action.hover",
											},
										}}
									>
										{tab.label}
									</Button>
								))}
							</Box>

							{view === "resumen" && (
								<Box mt={2}>
									<Card
										variant="outlined"
										sx={{
											borderRadius: "16px",
											border: "1px solid",
											borderColor: "divider",
											overflow: "hidden",
										}}
									>
										<Box
											sx={{
												bgcolor: "grey.900",
												color: "common.white",
												px: 3,
												py: 2,
											}}
										>
											<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
												{t("modal_chart_home.summary.title", "Resumo")}
											</Typography>
										</Box>

										<Box sx={{ p: 0 }}>
											{resumenSections.length === 0 ? (
												<Box sx={{ p: 2 }}>
													<Typography align="center">
														{t("modal_chart_home.summary.empty", "No hay datos de resumen para mostrar.")}
													</Typography>
												</Box>
											) : (
												<Box>
													{resumenSections.map((section) => (
														<Accordion
															key={section.type}
															disableGutters
															sx={{
																mb: 0,
																borderRadius: 0,
																borderTop: "1px solid",
																borderColor: "divider",
																boxShadow: "none",
																bgcolor: "background.paper",
															}}
														>
															<AccordionSummary
																expandIcon={<ExpandMoreIcon />}
																sx={{
																	bgcolor: "background.paper",
																	"& .MuiAccordionSummary-content": {
																		alignItems: "center",
																	},
																}}
															>
																<Typography sx={{ fontWeight: 600 }}>{sectionLabel(section.type)}</Typography>
															</AccordionSummary>

															<AccordionDetails sx={{ p: 0 }}>
																<TableContainer component={Paper} elevation={0}>
																	<Table size="small">
																		<TableHead>
																			<TableRow
																				sx={{
																					bgcolor: "grey.100",
																				}}
																			>
																				<TableCell sx={{ fontWeight: 600 }}>
																					{t("modal_chart_home.tab.summary.headers.contract_name", "Nome do contrato")}
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					{t("modal_chart_home.tab.summary.headers.real", "Real")}
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					{t("modal_chart_home.tab.summary.headers.target", "Objetivo")}
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					{t("modal_chart_home.tab.summary.headers.diff", "Diferença")}
																				</TableCell>
																				<TableCell sx={{ fontWeight: 600 }}>
																					{t("modal_chart_home.tab.summary.headers.recommendation", "Recomendação")}
																				</TableCell>
																			</TableRow>
																		</TableHead>
																		<TableBody>
																			{section.contracts.map((c, idx) => {
																				const diffNum = Number(c.diferencia);
																				let diffColor = "text.primary";

																				if (!isNaN(diffNum)) {
																					if (diffNum < 0) diffColor = "error.main";
																				}

																				return (
																					<TableRow key={`${c.id}-${idx}`}>
																						<TableCell>
																							<Typography>{c.nombre}</Typography>
																							{c.kronos_name && (
																								<Typography variant="body2" sx={{ color: "text.secondary" }}>
																									{c.kronos_name}
																								</Typography>
																							)}
																						</TableCell>

																						<TableCell>{c.real}</TableCell>
																						<TableCell>{c.objetivo}</TableCell>

																						<TableCell>
																							<Typography component="span" sx={{ fontWeight: 600, color: diffColor }}>
																								{c.diferencia}
																							</Typography>
																						</TableCell>

																						<TableCell sx={{ maxWidth: 420, whiteSpace: "normal" }}>
																							{c.recomendacion}
																						</TableCell>
																					</TableRow>
																				);
																			})}
																		</TableBody>
																	</Table>
																</TableContainer>
															</AccordionDetails>
														</Accordion>
													))}
												</Box>
											)}
										</Box>
									</Card>
								</Box>
							)}

							{view === "grafico" && (
								<Box
									sx={{
										position: "relative",
										borderRadius: "16px",
										border: "1px solid",
										borderColor: "divider",
										p: 2,
									}}
								>
									<Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
										<InfoIconButton
											placement="left"
											title={
												<Box>
													<Typography variant="body2">
														<Box component="span" sx={{ fontWeight: 700 }}>
															{t("new_charts.combinedCardTooltip.staffingTitle")}
														</Box>{" "}
														{t("new_charts.combinedCardTooltip.staffingDescription")}
													</Typography>

													<Typography variant="body2">
														<Box component="span" sx={{ fontWeight: 700 }}>
															{t("new_charts.combinedCardTooltip.coverageTitle")}
														</Box>{" "}
														{t("new_charts.combinedCardTooltip.coverageDescription")}
													</Typography>

													<Typography variant="body2">
														<Box component="span" sx={{ fontWeight: 700 }}>
															{t("new_charts.combinedCardTooltip.spaceTitle")}
														</Box>{" "}
														{t("new_charts.combinedCardTooltip.spaceDescription")}
													</Typography>
												</Box>
											}
										/>
									</Box>

									<Grid container spacing={2} justifyContent={hasIdeal ? "flex-start" : "center"}>
										{hasReal && (
											<Grid item xs={hasIdeal && hasReal ? 6 : 12}>
												<CombinedCard
													title={t("new_charts.real_staffing")}
													dotacion={totalNE}
													cobertura={coberturaReal}
													holgura={holguraReal}
													t={t}
												/>
											</Grid>
										)}

										{hasIdeal && (
											<Grid item xs={hasIdeal && hasReal ? 6 : 12}>
												<CombinedCard
													title={t("new_charts.target_staffing")}
													dotacion={totalSP}
													cobertura={coberturaIdeal}
													holgura={holguraIdeal}
													t={t}
												/>
											</Grid>
										)}

										<Grid item xs={12}>
											<LineChartMUI data={safeLineal} selectedMonths={safeMeses} />
										</Grid>
									</Grid>
								</Box>
							)}

							{view === "contratos" && (
								<Box
									sx={{
										borderRadius: "16px",
										border: "1px solid",
										borderColor: "divider",
										p: 1,
									}}
								>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<Box
												sx={{
													display: "flex",
													flexDirection: { xs: "column", md: "row" },
													gap: 1,
													alignItems: "flex-start",
												}}
											>
												<Box sx={{ flex: 1 }}>
													<Grid container spacing={2}>
														{SHIFT_SECTIONS.map((sec) => (
															<Grid item xs={12} md={4} key={sec}>
																<TarjetaDiferencia
																	titulo={labels[sec]}
																	diferencia={diferenciasContratos[sec] ?? t("new_charts.no_data")}
																	seccion={sec}
																	cargando={loading}
																	t={t}
																/>
															</Grid>
														))}
													</Grid>
												</Box>

												<Box
													sx={{
														display: "flex",
														justifyContent: { xs: "flex-end", md: "flex-start" },
														pt: { md: 1 },
													}}
												>
													<InfoIconButton
														placement="left"
														title={
															<Box>
																<Typography variant="body2" sx={{ mb: 1 }}>
																	{t("new_charts.contractsDifferenceTooltip.kpiDescription")}
																</Typography>

																<Typography variant="body2">
																	<Box component="span" sx={{ fontWeight: 700 }}>
																		{t("new_charts.contractsDifferenceTooltip.deficitTitle")}
																	</Box>{" "}
																	{t("new_charts.contractsDifferenceTooltip.deficitDescription")}
																</Typography>

																<Typography variant="body2">
																	<Box component="span" sx={{ fontWeight: 700 }}>
																		{t("new_charts.contractsDifferenceTooltip.excessTitle")}
																	</Box>{" "}
																	{t("new_charts.contractsDifferenceTooltip.excessDescription")}
																</Typography>
															</Box>
														}
													/>
												</Box>
											</Box>
										</Grid>

										<Grid item xs={12}>
											<Box
												sx={{
													display: "flex",
													flexDirection: { xs: "column", md: "row" },
													gap: 1,
													alignItems: "flex-start",
												}}
											>
												<Box sx={{ flex: 1 }}>
													<Grid container spacing={2}>
														<Grid item xs={12} md={6}>
															<Card
																variant="outlined"
																sx={{
																	mb: 2,
																	height: 400,
																	borderRadius: "16px",
																	border: "1px solid",
																	borderColor: "divider",
																}}
															>
																<CardContent
																	sx={{
																		width: "100%",
																		height: "100%",
																		display: "flex",
																		flexDirection: "column",
																		alignItems: "center",
																		justifyContent: "center",
																		backgroundColor: "background.paper",
																	}}
																>
																	<Box
																		sx={{
																			width: "100%",
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "space-between",
																			mb: 1,
																		}}
																	>
																		<Typography sx={{ fontWeight: 700 }}>{t("new_charts.initial_real")}</Typography>

																		<TotalPill label={totalPeopleLabel} total={totalPieNE} />
																	</Box>

																	<PieChartNE data={datosTortaNE} cargando={loading} t={t} />
																</CardContent>
															</Card>
														</Grid>

														<Grid item xs={12} md={6}>
															<Card
																variant="outlined"
																sx={{
																	mb: 2,
																	height: 400,
																	borderRadius: "16px",
																	border: "1px solid",
																	borderColor: "divider",
																}}
															>
																<CardContent
																	sx={{
																		width: "100%",
																		height: "100%",
																		display: "flex",
																		flexDirection: "column",
																		alignItems: "center",
																		justifyContent: "center",
																		backgroundColor: "background.paper",
																	}}
																>
																	<Box
																		sx={{
																			width: "100%",
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "space-between",
																			mb: 1,
																		}}
																	>
																		<Typography sx={{ fontWeight: 700 }}>{t("new_charts.objetive")}</Typography>

																		<TotalPill label={totalPeopleLabel} total={totalPieSP} />
																	</Box>

																	<PieChartSP data={datosTortaSP} cargando={loading} t={t} />
																</CardContent>
															</Card>
														</Grid>
													</Grid>
												</Box>

												<Box
													sx={{
														display: "flex",
														justifyContent: { xs: "flex-end", md: "flex-start" },
														pt: { md: 1 },
													}}
												>
													<InfoIconButton
														placement="left"
														title={
															<Typography variant="body2">
																{t("new_charts.contractsDistributionTooltip.description")}
															</Typography>
														}
													/>
												</Box>
											</Box>
										</Grid>

										<Grid item xs={12}>
											<Grid container direction="column" spacing={3}>
												{Object.keys(barrasPorSeccion).map((sec) => (
													<Grid item xs={12} key={sec}>
														<Card
															variant="outlined"
															sx={{
																mb: 2,
																height: 400,
																borderRadius: "16px",
																border: "1px solid",
																borderColor: "divider",
															}}
														>
															<CardContent
																sx={{
																	width: "100%",
																	height: "100%",
																	display: "flex",
																	flexDirection: "column",
																	alignItems: "center",
																	justifyContent: "center",
																	backgroundColor: "background.paper",
																}}
															>
																<Box
																	sx={{
																		width: "100%",
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		mb: 1,
																	}}
																>
																	<Typography align="center">{SECTION_LABELS(t)[sec]}</Typography>

																	<Button variant="outlined" size="small" onClick={() => handleOpenDetails(sec)}>
																		{t("aux.viewDetail")}
																	</Button>
																</Box>

																<BarCharts data={barrasPorSeccion[sec]} seccion={sec} cargando={loading} t={t} />
															</CardContent>
														</Card>
													</Grid>
												))}
											</Grid>
										</Grid>
									</Grid>
								</Box>
							)}

							{view === "tabla" && (
								<Box mt={2}>
									<Grid container spacing={2} mb={4}>
										{SHIFT_SECTIONS.map((sec) => (
											<Grid item xs={12} md={4} key={sec}>
												<TarjetaLogrado
													titulo={labels[sec]}
													diferencia={diferencias[sec] ?? t("new_charts.no_data")}
													logrado={logrados[sec] ?? t("new_charts.no_data")}
													seccion={sec}
													cargando={loading}
													t={t}
												/>
											</Grid>
										))}
									</Grid>
									<Box sx={{ height: 400, width: "100%" }}>
										<DataGrid
											rows={filasTabla}
											columns={columns}
											pageSize={5}
											rowsPerPageOptions={[5, 10]}
											components={{ Toolbar: GridToolbar }}
											loading={loading}
											sx={{
												"& .col-diferencia": {
													fontWeight: 600,
												},
											}}
										/>
									</Box>
								</Box>
							)}

							<Box mt={3} display="flex" justifyContent="center">
								<Button
									variant="outlined"
									onClick={onClose}
									sx={{
										borderRadius: "999px",
										border: "2px, solid",
										padding: "6px 24px",
										textTransform: "none",
									}}
								>
									{t("modal_chart_home.button.close")}
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Modal>

			<Dialog open={openDetailModal} onClose={handleCloseDetails} fullWidth maxWidth="lg">
				<DialogTitle sx={{ fontWeight: 700 }}>
					{t("new_charts.contract_gap_detail_title", {
						section: labels[selectedSection] || selectedSection,
					})}
				</DialogTitle>

				<DialogContent>
					<TableContainer
						component={Paper}
						sx={{
							mt: 2,
							borderRadius: 4,
							overflow: "hidden",
							border: "1px solid",
							borderColor: "divider",
							boxShadow: "none",
							"& .MuiTableCell-root": {
								borderBottom: "1px solid",
								borderColor: "divider",
							},
							"& .MuiTableHead-root": {
								backgroundColor: "divider",
							},
							"& .MuiTableHead-root .MuiTableCell-root": {
								fontWeight: 700,
							},
						}}
					>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>{t("new_charts.contract")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.lunes")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.martes")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.miercoles")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.jueves")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.viernes")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.sabado")}</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>{t("aux.days.domingo")}</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{contractsLoading ? (
									<TableRow>
										<TableCell colSpan={8} align="center">
											{t("aux.loading")}
										</TableCell>
									</TableRow>
								) : contractDetails[selectedSection]?.length > 0 ? (
									contractDetails[selectedSection].map((row) => (
										<TableRow key={row.id}>
											<TableCell>{row.contrato}</TableCell>
											<TableCell>{row.lunes}</TableCell>
											<TableCell>{row.martes}</TableCell>
											<TableCell>{row.miercoles}</TableCell>
											<TableCell>{row.jueves}</TableCell>
											<TableCell>{row.viernes}</TableCell>
											<TableCell>{row.sabado}</TableCell>
											<TableCell>{row.domingo}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={8} align="center">
											{t("new_charts.no_data")}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>

				<DialogActions sx={{ p: 2 }}>
					<Button variant="outlined" onClick={handleCloseDetails}>
						{t("buttons.cancel")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
